import { Fragment, useEffect, useState } from "react";
import { transparentize } from "polished";
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  ListSubheader,
  makeStyles
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { Color } from "../theme/color";

export interface ListSelectorItem {
  group?: string;
  label: string;
  value: string;
}

export type ListSelectorProps = {
  items: Array<ListSelectorItem>;
  value: string[];
  onChange: (value: string[]) => void;
  multi: boolean;
  withSearch?: boolean;
  grouped?: boolean;
  searchAutofocus?: boolean;
};

export function ListSelector(props: ListSelectorProps) {
  const { searchAutofocus = true } = props;

  const [search, setSearch] = useState("");
  const [grouped, setGrouped] = useState<Record<string, ListSelectorItem[]>>({});
  const [filtered, setFiltered] = useState<Record<string, ListSelectorItem[]>>({});

  const classes = useStyles();

  const handleItemSelect = (userId: string) => {
    if (props.multi) {
      if (props.value.includes(userId)) {
        props.onChange(props.value.filter((id) => id !== userId));
      } else {
        props.onChange([...props.value, userId]);
      }
    } else {
      props.onChange([userId]);
    }
  };

  useEffect(() => {
    const grouped = props.items.reduce((acc, item) => {
      const g = item.group || "";
      if (!acc[g]) {
        acc[g] = [];
      }
      acc[g].push(item);
      return acc;
    }, {} as Record<string, ListSelectorItem[]>);

    setGrouped(grouped);
    setSearch("");
  }, [props.items]);

  useEffect(() => {
    if (!props.withSearch) {
      setSearch("");
    }
  }, [props.withSearch]);

  useEffect(() => {
    const filtered = Object.entries(grouped).reduce((acc, [team, items]) => {
      acc[team] = items.filter((item) =>
        item.label.toLowerCase().includes(search.toLowerCase())
      );
      return acc;
    }, {} as Record<string, ListSelectorItem[]>);

    for (const team in filtered) {
      if (filtered[team].length === 0) {
        delete filtered[team];
      }
    }

    setFiltered(filtered);
  }, [search]);

  const items = search ? filtered : grouped;

  return (
    <div>
      {props.withSearch && (
        <Box>
          <TextField
            autoFocus={searchAutofocus}
            fullWidth
            variant="outlined"
            placeholder="Search"
            InputProps={{
              startAdornment: <Search style={{ color: Color.LightGray }} />
            }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
      )}

      {Object.entries(items).length === 0 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="200px"
          color={Color.Body}
        >
          No items found
        </Box>
      )}

      <List dense>
        {Object.entries(items).map(([team, items]) => (
          <Fragment key={team}>
            <ListSubheader
              classes={{
                root: classes.listItemSubheaderRoot,
                gutters: classes.listItemSubheaderGutters
              }}
            >
              {team || "(saknas)"}
            </ListSubheader>

            {items.map((item) => {
              return (
                <ListItem
                  key={item.value}
                  button
                  classes={{
                    root: classes.listItemRoot,
                    gutters: classes.listItemGutters,
                    button: classes.listItemButton
                  }}
                  onClick={() => {
                    handleItemSelect(item.value);
                  }}
                >
                  {props.multi && (
                    <ListItemIcon classes={{ root: classes.listItemIcon }}>
                      <Checkbox
                        id={item.value}
                        classes={{ root: classes.listItemCheckbox }}
                        checked={props.value.includes(item.value)}
                        onChange={(e) => {
                          handleItemSelect(item.value);
                        }}
                        size="small"
                      />
                    </ListItemIcon>
                  )}

                  <ListItemText>{item.label}</ListItemText>
                </ListItem>
              );
            })}
          </Fragment>
        ))}
      </List>
    </div>
  );
}

const useStyles = makeStyles(() => ({
  listItemCheckbox: {
    padding: 0
  },
  listItemIcon: {
    minWidth: 0,
    paddingRight: "8px"
  },
  listItemRoot: {
    // borderBottom: "1px solid #e0e0e0",
    paddingTop: "8px",
    paddingBottom: "8px"
  },
  listItemSubheaderRoot: {
    background: "white",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "16px",
    color: Color.Headings
  },
  listItemGutters: {
    padding: "4px"
  },
  listItemSubheaderGutters: {
    padding: "16px 4px"
    // paddingBottom: 0,
    //
    // "&:not(:first-child)": {
    //   marginTop: "8px"
    // }
  },
  listItemButton: {
    "&:hover": {
      backgroundColor: transparentize(0.85, Color.PrimaryLight)
    }
  }
}));
